/**
 * @file chat.service.ts
 * @description This service manages the chat functionality, including SignalR connection,
 * CSRF-protected messaging, streaming updates, and session recovery.
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, LOCALE_ID } from '@angular/core';
import { BaseApiService } from '@core/services/base-api.service';
import * as signalR from '@microsoft/signalr';
import { HubConnection } from '@microsoft/signalr';
import { firstValueFrom, Subject } from 'rxjs';

/**
 * @interface ChatMessage
 * @description Represents a single chat message.
 * @property {string} user - The sender of the message (e.g., 'Me', 'Bueno').
 * @property {string} message - The content of the message.
 */
interface ChatMessage {
  user: string;
  message: string;
}

interface SessionInfoResponse {
  csrfToken: string;
  connectionTicket: string;
  hasHistory: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private httpClient = inject(HttpClient);
  private locale = inject(LOCALE_ID);
  private baseApiUrl = inject<string>('BASE_AI_URL' as any);
  private hubsBaseUrl = inject<string>('BASE_HUBS_URL' as any);
  private baseApiService = inject(BaseApiService);
  private hubConnection?: HubConnection;

  /**
   * @property {string | null} sessionId - The unique session ID for the SignalR connection.
   */
  public sessionId: string | null = null;

  private csrfToken: string | null = null;
  private shouldRequestHistory = false;
  private startPromise: Promise<void> | null = null;
  private readonly csrfHeaderName = 'X-Csrf-Token';

  /**
   * @private
   * @property {Subject<ChatMessage>} messageSource - Source for complete chat messages (user and IA).
   */
  private messageSource = new Subject<ChatMessage>();
  /**
   * @property {Observable<ChatMessage>} message$ - Observable for complete chat messages.
   */
  message$ = this.messageSource.asObservable();

  /**
   * @private
   * @property {Subject<string>} currentMessageSource - Source for partial IA responses, used for typing simulation.
   */
  private currentMessageSource = new Subject<string>();
  /**
   * @property {Observable<string>} currentMessage$ - Observable for partial IA responses.
   */
  currentMessage$ = this.currentMessageSource.asObservable();

  /**
   * @private
   * @property {Subject<boolean>} isResponding - Source to indicate if the IA is currently responding/typing.
   */
  private isResponding = new Subject<boolean>();
  /**
   * @property {Observable<boolean>} isResponding$ - Observable for the IA's responding status.
   */
  isResponding$ = this.isResponding.asObservable();

  /**
   * @private
   * @property {string} currentIAResponse - Accumulates the IA's response chunks before emitting the complete message.
   */
  private currentIAResponse: string = '';
  private pendingUserMessages: Array<{ text: string; timestamp: number }> = [];
  private readonly pendingUserMessageTtlMs = 5000;

  /**
   * @method startConnection
   * @description Fetches session metadata, starts the SignalR connection with the one-time ticket,
   * and optionally requests history for existing sessions.
   */
  startConnection(): void {
    const connectionState = this.hubConnection?.state;
    const hasActiveConnection =
      connectionState === signalR.HubConnectionState.Connected ||
      connectionState === signalR.HubConnectionState.Connecting ||
      connectionState === signalR.HubConnectionState.Reconnecting;

    if (this.startPromise || hasActiveConnection) {
      return;
    }

    this.startPromise = this.initializeSessionAndConnection()
      .catch(() => undefined)
      .finally(() => {
        this.startPromise = null;
      });
  }

  /**
   * @method sendMessage
   * @description Sends a user's message to the chat. It first echoes the message to the UI
   * and then dispatches it to the backend API with the CSRF token.
   * @param {string} user - The user sending the message (e.g., 'Me').
   * @param {string} message - The content of the message to send.
   */
  sendMessage(user: string, message: string): void {
    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      return;
    }

    if (!this.csrfToken) {
      console.error('CSRF token is not available. Cannot send message to API.');
      this.startConnection();
      return;
    }

    const echoMessage: ChatMessage = { user, message: trimmedMessage };
    this.messageSource.next(echoMessage);
    this.pendingUserMessages.push({ text: trimmedMessage, timestamp: Date.now() });
    this.isResponding.next(true);

    const payload = {
      messageText: trimmedMessage,
      culture: this.locale || 'en',
    };

    let headers = new HttpHeaders().set('Content-Type', 'application/json');
    headers = headers.set(this.csrfHeaderName, this.csrfToken);

    const apiUrl = `${this.baseApiUrl}/ai/ProcessChatMessage`;
    this.httpClient.post(apiUrl, payload, { headers, withCredentials: true }).subscribe({
      next: () => {
        this.currentIAResponse = '';
      },
      error: error => {
        this.removePendingUserMessage(trimmedMessage);
        this.isResponding.next(false);
        throw error;
      },
    });
  }

  private async initializeSessionAndConnection(): Promise<void> {
    try {
      const sessionInfo = await firstValueFrom(
        this.httpClient.get<SessionInfoResponse>(`${this.baseApiUrl}/ai/GetSessionInfo`, {
          withCredentials: true,
        })
      );

      if (!sessionInfo.csrfToken || !sessionInfo.connectionTicket) {
        throw new Error('Incomplete session info response received from GetSessionInfo.');
      }

      this.csrfToken = sessionInfo.csrfToken;
      this.shouldRequestHistory = sessionInfo.hasHistory;

      await this.buildAndStartHubConnection(sessionInfo.connectionTicket);

      if (this.shouldRequestHistory) {
        this.requestHistory();
        this.shouldRequestHistory = false;
      }
    } catch (error) {
      throw error;
    }
  }

  private async buildAndStartHubConnection(connectionTicket: string): Promise<void> {
    if (!connectionTicket) {
      throw new Error('Connection ticket is required to start the chat hub.');
    }

    await this.stopCurrentConnection();

    const hubUrl = `${this.hubsBaseUrl}/aiMessaging?connectionticket=${encodeURIComponent(connectionTicket)}`;

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        withCredentials: true,
      })
      .withAutomaticReconnect()
      .build();

    this.registerHubEventHandlers(this.hubConnection);

    try {
      await this.hubConnection.start();
      this.sessionId = this.hubConnection.connectionId ?? null;
      console.log('SignalR connection started with Session ID:', this.sessionId);
    } catch (startError) {
      this.sessionId = null;
      throw startError;
    }
  }

  private registerHubEventHandlers(connection: HubConnection): void {
    connection.on('ReceiveUserMessage', payload => {
      const chatMessage = this.tryBuildChatMessage(payload, 'Me');
      if (chatMessage && this.shouldEmitUserMessage(chatMessage.message)) {
        this.messageSource.next(chatMessage);
      }
    });

    connection.on('ReceiveAssistantMessage', payload => {
      if (payload.includes('<start_of_turn>model')) {
        payload = payload.replace('<start_of_turn>model', '');
      }
      if (payload.includes('<start_of_turn>')) {
        payload = payload.replace('<start_of_turn>', '');
      }
      const chatMessage = this.tryBuildChatMessage(payload, 'Bueno');
      if (chatMessage) {
        this.handleAssistantMessage(chatMessage);
      }
    });

    connection.on('ReceiveAssistantMessageStream', chunk => {
      this.handleAssistantMessageStreamChunk(chunk);
    });

    connection.onreconnecting(() => {
      this.isResponding.next(false);
      this.sessionId = null;
    });

    connection.onreconnected(connectionId => {
      this.sessionId = connectionId ?? this.hubConnection?.connectionId ?? null;
    });

    connection.onclose(() => {
      if (this.hubConnection !== connection) {
        return;
      }

      this.hubConnection = undefined;
      this.sessionId = null;
      this.startConnection();
    });
  }

  private requestHistory(): void {
    if (!this.csrfToken) {
      return;
    }

    let headers = new HttpHeaders().set(this.csrfHeaderName, this.csrfToken);

    this.httpClient
      .get(`${this.baseApiUrl}/ai/RequestHistory?connectionId=${this.sessionId}`, { headers, withCredentials: true })
      .subscribe({
        error: error => {
          throw error;
        },
      });
  }

  private handleAssistantMessage(chatMessage: ChatMessage): void {
    this.currentIAResponse = '';
    this.isResponding.next(false);
    this.messageSource.next(chatMessage);
  }

  private handleAssistantMessageStreamChunk(chunk: unknown): void {
    if (typeof chunk !== 'string') {
      return;
    }

    if (chunk === '__error__') {
      this.currentIAResponse = '';
      this.isResponding.next(false);
      return;
    }

    if (chunk === '__end__') {
      if (this.currentIAResponse) {
        this.messageSource.next({ user: 'Bueno', message: this.currentIAResponse });
      }

      this.currentIAResponse = '';
      this.isResponding.next(false);
      return;
    }

    this.isResponding.next(true);
    this.currentIAResponse += chunk;
    this.currentMessageSource.next(chunk);
  }

  private async stopCurrentConnection(): Promise<void> {
    if (!this.hubConnection) {
      return;
    }

    const connectionToStop = this.hubConnection;
    this.hubConnection = undefined;
    this.sessionId = null;

    try {
      await connectionToStop.stop();
    } catch (stopError) {
      console.warn('Error while stopping previous hub connection', stopError);
    }
  }

  private shouldEmitUserMessage(message: string): boolean {
    if (!this.pendingUserMessages.length) {
      return true;
    }

    const now = Date.now();
    this.pendingUserMessages = this.pendingUserMessages.filter(
      entry => now - entry.timestamp <= this.pendingUserMessageTtlMs
    );

    const matchIndex = this.pendingUserMessages.findIndex(entry => entry.text === message);
    if (matchIndex === -1) {
      return true;
    }

    this.pendingUserMessages.splice(matchIndex, 1);
    return false;
  }

  private removePendingUserMessage(message: string): void {
    const matchIndex = this.pendingUserMessages.findIndex(entry => entry.text === message);
    if (matchIndex !== -1) {
      this.pendingUserMessages.splice(matchIndex, 1);
    }
  }

  clearConversation(): void {
    this.pendingUserMessages = [];
    this.currentIAResponse = '';
    this.isResponding.next(false);
  }

  private tryBuildChatMessage(payload: unknown, fallbackUser: string): ChatMessage | null {
    if (typeof payload === 'string') {
      return {
        user: fallbackUser,
        message: payload,
      };
    }

    if (payload && typeof payload === 'object') {
      const candidate = payload as { user?: string; message?: string; messageText?: string; content?: string };
      const message =
        typeof candidate.message === 'string'
          ? candidate.message
          : typeof candidate.messageText === 'string'
            ? candidate.messageText
            : typeof candidate.content === 'string'
              ? candidate.content
              : null;

      if (!message) {
        return null;
      }

      const user = typeof candidate.user === 'string' ? candidate.user : fallbackUser;
      return { user, message };
    }

    return null;
  }
}
